import React, { useEffect, useMemo, useState } from "react";
import { buildBaseKey, withinRangeSec, ageShort } from "./historyUtils";
import AssetLabel from "../components/common/AssetLabel";

export default function HistorySection({
  section,
  allEvents,
  range,
  occ2,
  insights,
  openPopup,
  collapseCommand,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const title = (section?.title || "").trim();
  const desc = section?.desc || "";

  useEffect(() => {
    if (collapseCommand) {
      setCollapsed(collapseCommand.collapsed);
    }
  }, [collapseCommand]);

  const sectionEventsAll = useMemo(() => {
    return allEvents.filter((e) => e.sectionTitle === title);
  }, [allEvents, title]);

  const sectionEventsVisible = useMemo(() => {
    return sectionEventsAll.filter((e) => withinRangeSec(e.ts, range));
  }, [sectionEventsAll, range]);

  const pills = useMemo(() => {
    // keys visible in time range
    const visibleKeys = new Set(sectionEventsVisible.map((e) => e.baseKey));

    // count occurrences across whole history (for this section), but only for keys visible in range
    const map = new Map();
    for (const ev of sectionEventsAll) {
      if (!visibleKeys.has(ev.baseKey)) continue;
      const p = map.get(ev.baseKey);
      if (!p) {
        map.set(ev.baseKey, {
          baseKey: ev.baseKey,
          cur: ev.cur,
          bull: !!ev.bull,
          bear: !!ev.bear,
          assetType: ev.assetType,
          count: 1,
          lastTs: ev.ts || 0, // we'll refine below
        });
      } else {
        p.count += 1;
      }
    }

    // set lastTs from *visible* events (so sorting reflects selected time range)
    for (const ev of sectionEventsVisible) {
      const p = map.get(ev.baseKey);
      if (p) p.lastTs = Math.max(p.lastTs || 0, ev.ts || 0);
    }

    let arr = Array.from(map.values());

    if (occ2) arr = arr.filter((p) => p.count >= 2);

    // Sort: smallest time since last event first (i.e. newest lastTs first)
    arr.sort((a, b) => (b.lastTs || 0) - (a.lastTs || 0));

    return arr;
  }, [sectionEventsAll, sectionEventsVisible, occ2]);

  const openForPill = (pill) => {
    // sectionEvents = events in this section (for arrows: switch between currencies like Insights)
    const sectionEventsForArrows = [...sectionEventsAll].sort((a, b) => (b.ts || 0) - (a.ts || 0));
    let idx = sectionEventsForArrows.findIndex((e) => e.cur === pill.cur && e.baseKey === pill.baseKey);
    if (idx < 0) idx = sectionEventsForArrows.findIndex((e) => e.cur === pill.cur);
    if (idx < 0) idx = 0;

    const fullInfo = {
      index: idx,
      sectionEvents: sectionEventsForArrows,
      sectionTitleWhenFromHistory: title,
      fullHistoryFlat: allEvents,
    };
    openPopup(fullInfo);
  };

  if (!pills.length) return null;

  return (
    <div className="historySection">
      <div className="historySectionHead" onClick={() => setCollapsed((c) => !c)} title={desc}>
        <div className="historySectionTitle">
          <span className="historyChevron">{collapsed ? "▸" : "▾"}</span>
          {title}
          <span className="historySectionCount">({pills.length})</span>
        </div>
        <div className="historySectionHint">
          {sectionEventsVisible.length ? "Updated " + ageShort(Math.max(...sectionEventsVisible.map(e => e.ts || 0))) : ""}
        </div>
      </div>

      {!collapsed && (
        <div className="historyPillGrid">
          {pills.map((p) => {
            const color = p.bull && p.bear ? "both" : p.bull ? "bull" : p.bear ? "bear" : "neutral";
            return (
              <button
                key={p.baseKey + p.cur}
                className={`historyPill historyPill-${color}`}
                onClick={(ev) => {
                  ev.stopPropagation();
                  openForPill(p);
                }}
                type="button"
              >
                <span className="historyPillCur">
                  <AssetLabel symbol={p.cur} assetType={p.assetType} className="assetLabelCompact" />
                </span>
                {p.count >= 2 ? <span className="historyPillCount">{p.count}x</span> : null}
                <span className="historyPillAge">{ageShort(p.lastTs)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
